import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов
 */
const ComparisonSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-fuchsia-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты расчета плитки для сравнения.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-fuchsia-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {getSuffixForCount(savedCalculations.length, 'вариант', 'варианта', 'вариантов')}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.map((calc) => (
          <div 
            key={calc.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calc.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calc.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calc.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Размеры:</span>
                <span className="font-medium">
                  {calc.formData.roomWidth}×{calc.formData.roomLength} м
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Плитка:</span>
                <span>
                  {calc.formData.tileWidth}×{calc.formData.tileLength} см
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Количество:</span>
                <span>
                  {formatNumber(calc.results.totalTilesNeeded)} шт. ({calc.results.packagesNeeded} уп.)
                </span>
              </div>
              <div className="flex justify-between">
                <span>Стоимость:</span>
                <span className="font-semibold text-fuchsia-600 dark:text-fuchsia-400">
                  {formatCurrency(calc.results.totalCost, 0, 'ru-RU')}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения существительных
const getSuffixForCount = (count, one, few, many) => {
  if (count % 10 === 1 && count % 100 !== 11) {
    return one;
  } else if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) {
    return few;
  } else {
    return many;
  }
};

export default ComparisonSidebar;